import {useCallback, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {useTranslation} from 'react-i18next'
import {
  Box,
  Text,
  Image,
  Skeleton,
  CloseButton,
  Input,
  Flex,
} from '@chakra-ui/react'
import {Icon} from '@chakra-ui/icons'
import {DirectUpload} from '@rails/activestorage'

import {UploadIcon} from '../../icons'

const LogoUpload = ({
  logoUrl,
  isLoading,
  directUploadsUrl,
  onUpdate,
  onDestroy,
}) => {
  const {t} = useTranslation('logoUpload')

  const [progress, setProgress] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [downloaded, setDownloaded] = useState(false)
  const [removed, setRemoved] = useState(false)
  const [droppable, setDroppable] = useState(false)

  const transfer = useCallback(
    async (file) => {
      if (!file) {
        return
      }

      setDroppable(false)
      setRemoved(false)
      setUploading(true)
      setDownloaded(false)

      const upload = new DirectUpload(file, directUploadsUrl, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener('progress', ({total, loaded}) => {
            setUploading(total !== loaded)
            setProgress(loaded / total)
          })
        },
      })

      upload.create((error, blob) => {
        if (error) {
          return
        }

        onUpdate(blob.signed_id)
      })
    },
    [directUploadsUrl, onUpdate],
  )

  const onDrop = useCallback(
    async (event) => {
      event.preventDefault()
      const [file] = event.dataTransfer.files
      await transfer(file)
    },
    [transfer],
  )

  const onFileUpload = useCallback(
    async (event) => {
      const [file] = event.target.files
      await transfer(file)
    },
    [transfer],
  )

  const onRemove = useCallback(() => {
    setRemoved(true)
    setProgress(0)
    onDestroy()
  }, [onDestroy])

  const onDragOver = (event) => {
    event.preventDefault()
    if (droppable) {
      return
    }
    setDroppable(true)
  }

  const onDragLeave = (event) => {
    event.preventDefault()
    setDroppable(false)
  }

  const inputRef = useRef(null)

  const showImage = !uploading
  const showSkeleton = !removed && (!downloaded || isLoading)

  return (
    <Box bgColor="mld.neutral.0" overflow="hidden" borderRadius="lg">
      {logoUrl ? (
        <Box textAlign="center">
          <Flex alignItems="top" justifyContent="center" p="md">
            {showImage && (
              <>
                <Box width="32px" />
                <Box maxHeight="80px" overflow="hidden">
                  {showSkeleton && <Skeleton width="200px" height="80px" />}
                  <Image
                    src={logoUrl}
                    width="200px"
                    height="80px"
                    onLoad={() => {
                      setDownloaded(true)
                    }}
                    className="fs-exclude"
                  />
                </Box>
                <Box>
                  <CloseButton onClick={onRemove} />
                </Box>
              </>
            )}
          </Flex>
        </Box>
      ) : (
        <Box>
          <Box
            textAlign="center"
            bgColor={droppable ? 'mld.neutral.100' : null}
            borderColor={droppable ? null : 'mld.neutral.500'}
            borderWidth="2px"
            borderRadius="lg"
            p="md"
            onClick={() => inputRef.current.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
          >
            <Icon as={UploadIcon} boxSize="6" />
            <Text fontSize="sm" fontWeight="medium">
              {t('upload.prompt')}
            </Text>
            {uploading ? (
              <Box>
                <Text>{t('upload.inProgress')}</Text>
                <Text>{Math.round(progress * 100, 2)}%</Text>
              </Box>
            ) : (
              <Box>
                <Text color="mld.neutral.800" fontSize="xs">
                  {t('upload.subtext')}
                </Text>
                <Text color="mld.neutral.800" fontSize="xs">
                  {t('upload.fileTypes')}
                </Text>
              </Box>
            )}
            <Input hidden type="file" ref={inputRef} onChange={onFileUpload} />
          </Box>
        </Box>
      )}
    </Box>
  )
}

LogoUpload.propTypes = {
  logoUrl: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  directUploadsUrl: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDestroy: PropTypes.func.isRequired,
}

LogoUpload.defaultProps = {
  logoUrl: null,
}

export default LogoUpload
